import { CheckCircle2, Circle, Clock } from 'lucide-react';

const InvestorJourneyProgress = ({ user, commitments = [] }) => {
  // Don't show for admins
  const isAdmin = ['super_admin', 'fund_director', 'analyst', 'compliance'].includes(user?.role);
  if (isAdmin) return null;

  // Determine journey stage
  const kycStatus = user?.verification_status;
  const hasCommitments = commitments.length > 0;
  const hasPendingSignature = commitments.some(c => c.status === 'awaiting_signature');
  const hasSignedCommitment = commitments.some(c => ['countersign_pending', 'approved', 'confirmed'].includes(c.status));
  const hasApprovedCommitment = commitments.some(c => ['approved', 'funds_pending', 'confirmed'].includes(c.status));
  const hasConfirmedCommitment = commitments.some(c => c.status === 'confirmed');

  const stages = [
    {
      id: 'kyc',
      label: 'Verificación KYC',
      description: kycStatus === 'approved' ? 'Completado' : kycStatus === 'pending' ? 'Pendiente de revisión' : 'Por completar',
      status: kycStatus === 'approved' ? 'complete' : kycStatus === 'pending' ? 'pending' : 'incomplete'
    },
    {
      id: 'commit',
      label: 'Compromiso',
      description: hasCommitments ? `${commitments.length} compromiso(s)` : 'Sin compromisos',
      status: hasCommitments ? 'complete' : kycStatus === 'approved' ? 'current' : 'locked'
    },
    {
      id: 'sign',
      label: 'Firma',
      description: hasSignedCommitment ? 'Firmado' : hasPendingSignature ? 'Pendiente firma' : 'Por firmar',
      status: hasSignedCommitment ? 'complete' : hasPendingSignature ? 'current' : hasCommitments ? 'pending' : 'locked'
    },
    {
      id: 'fund',
      label: 'Fondeo',
      description: hasApprovedCommitment ? 'Listo para fondeo' : 'Por fondear',
      status: hasConfirmedCommitment ? 'complete' : hasApprovedCommitment ? 'current' : 'locked'
    },
    {
      id: 'active',
      label: 'Activo',
      description: hasConfirmedCommitment ? 'Inversión activa' : 'Pendiente',
      status: hasConfirmedCommitment ? 'complete' : 'locked'
    }
  ];

  const getStageIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircle2 className="h-6 w-6 text-emerald-500" />;
      case 'current':
        return <Clock className="h-6 w-6 text-primary animate-pulse" />;
      case 'pending':
        return <Clock className="h-6 w-6 text-amber-500" />;
      default:
        return <Circle className="h-6 w-6 text-muted-foreground/30" />;
    }
  };

  const getStageClass = (status) => {
    switch (status) {
      case 'complete':
        return 'border-emerald-500/30 bg-emerald-500/5';
      case 'current':
        return 'border-primary/30 bg-primary/5 ring-2 ring-primary/20';
      case 'pending':
        return 'border-amber-500/30 bg-amber-500/5';
      default:
        return 'border-muted bg-muted/20 opacity-50';
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground">Su Progreso de Inversión</h3>
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {stages.map((stage, index) => (
          <div key={stage.id} className="flex items-center">
            <div className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${getStageClass(stage.status)}`}>
              {getStageIcon(stage.status)}
              <div className="min-w-0">
                <p className="text-xs font-medium whitespace-nowrap">{stage.label}</p>
                <p className="text-[10px] text-muted-foreground truncate max-w-20">{stage.description}</p>
              </div>
            </div>
            {index < stages.length - 1 && (
              <div className={`w-4 h-0.5 mx-1 ${
                stage.status === 'complete' ? 'bg-emerald-500' : 'bg-muted'
              }`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InvestorJourneyProgress;
